import React, { useRef, useCallback, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { flexRender } from '@tanstack/react-table';
import { useLegacyTable as useReactTable, getCoreRowModel } from '@tanstack/react-table/legacy';
import { useUser } from '../../controllers/UserContext';
import ManageFieldsButton from './TableManager/ManageFieldsModal';
import * as defaultCustomFields from '../../utilities/defaultCustomTableField';
import { CaretDownFill, CaretUpFill } from 'react-bootstrap-icons';

const TABLE_DEFAULTS_MAP = {
  portfolios: defaultCustomFields.defaultPortfolioField,
  portfoliocategory: defaultCustomFields.defaultPortfolioCategoryField,
  portfoliocomments: defaultCustomFields.defaultPortfolioCommentsField,
};

// Table-wise specific keys allowed in Sort By menu (if specified for a table, only these keys are sortable)
export const TABLE_WISE_ALLOWED_SORT_KEYS = {
  portfolios: ["title", "portfoliocategory", "comment_count", "createdAt"],
  portfoliocategory: ["lowercase_category", "title", "createdAt", "portfolio_count"],
  portfoliocomments: ["title", "comment_count", "createdAt"],
};

const sanitizeWidthClasses = (cls = '') => {
  return cls || '';
};

const parseSize = (val, fallback = 150) => {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
  }
  return fallback;
};

export const isAscendingOrder = (order) => {
  if (!order) return false;
  if (typeof order === 'string') {
    const upper = order.trim().toUpperCase();
    return upper === 'ASC' || upper === 'A-Z' || upper === '1' || upper === 'TRUE';
  }
  if (typeof order === 'object') {
    if (order.orderByValue !== undefined) {
      const upper = String(order.orderByValue).trim().toUpperCase();
      return upper === 'ASC' || upper === 'A-Z' || upper === '1' || upper === 'TRUE';
    }
    if (order.order_by !== undefined) {
      const upper = String(order.order_by).trim().toUpperCase();
      return upper === 'ASC' || upper === 'A-Z' || upper === '1' || upper === 'TRUE';
    }
    if (order.isDecending !== undefined) {
      return order.isDecending === false || order.isDecending === 'false';
    }
    if (order.isDescending !== undefined) {
      return order.isDescending === false || order.isDescending === 'false';
    }
    if (order.isAscending !== undefined) {
      return order.isAscending === true || order.isAscending === 'true';
    }
  }
  return false;
};

const DEFAULT_COLUMN_WIDTH_BY_COUNT = {
  3: '220px',
  4: '200px',
  5: '190px',
  6: '180px',
  7: '170px',
  8: '160px',
  9: '160px',
  10: '150px',
};

const DEFAULT_COLUMN_MAX_WIDTH_BY_COUNT = {
  3: '450px',
  4: '400px',
  5: '350px',
  6: '320px',
  7: '300px',
  8: '280px',
  9: '260px',
  10: '250px',
};

const getWidthByColumnCount = (count, map = DEFAULT_COLUMN_WIDTH_BY_COUNT, fallbackVal = 220) => {
  if (!map || typeof map !== 'object') return fallbackVal;
  if (map[count]) {
    return parseSize(map[count]);
  }
  const numericKeys = Object.keys(map).map(Number).filter((n) => !isNaN(n)).sort((a, b) => a - b);
  if (numericKeys.length === 0) return fallbackVal;
  if (count <= numericKeys[0]) return parseSize(map[numericKeys[0]]);
  if (count >= numericKeys[numericKeys.length - 1]) return parseSize(map[numericKeys[numericKeys.length - 1]]);

  for (let i = 0; i < numericKeys.length - 1; i++) {
    const lower = numericKeys[i];
    const upper = numericKeys[i + 1];
    if (count >= lower && count <= upper) {
      const lowerVal = parseSize(map[lower]);
      const upperVal = parseSize(map[upper]);
      const ratio = (count - lower) / (upper - lower);
      return Math.round(lowerVal + ratio * (upperVal - lowerVal));
    }
  }
  return parseSize(map[numericKeys[0]]);
};

/**
 * Custom hook for universal Shift + Click checkbox range selection.
 */
export const useShiftSelect = (
  data = [],
  selectedRowsId = [],
  setSelectedRowsId = () => { },
  options = {}
) => {
  const lastSelectedIndexRef = useRef(null);

  const opts = typeof options === 'string' ? { keyField: options } : options || {};
  const { keyField = '_id', isSelectable } = opts;

  const getItemId = useCallback(
    (item) => (typeof keyField === 'function' ? keyField(item) : item?.[keyField]),
    [keyField]
  );

  const handleSelectRow = useCallback(
    (e, id) => {
      if (id === 'all') {
        const selectableRows = isSelectable ? data.filter(isSelectable) : data;
        const selectableIds = selectableRows
          .map(getItemId)
          .filter((x) => x !== undefined && x !== null && x !== '');

        const isAllSelected =
          selectableIds.length > 0 &&
          selectableIds.every((rowId) => selectedRowsId.includes(rowId));

        if (isAllSelected) {
          setSelectedRowsId((prev) => prev.filter((prevId) => !selectableIds.includes(prevId)));
        } else {
          setSelectedRowsId((prev) => Array.from(new Set([...prev, ...selectableIds])));
        }
        lastSelectedIndexRef.current = null;
        return;
      }

      const currIndex = data.findIndex((item) => getItemId(item) === id);
      if (currIndex === -1) {
        const isSelected = selectedRowsId.includes(id);
        if (isSelected) {
          setSelectedRowsId((prev) => prev.filter((prevId) => prevId !== id));
        } else {
          setSelectedRowsId((prev) => [...prev, id]);
        }
        return;
      }

      const isShiftPressed = Boolean(
        e?.shiftKey ||
        e?.nativeEvent?.shiftKey ||
        (typeof window !== 'undefined' && window.event && window.event.shiftKey)
      );

      const lastIndex = lastSelectedIndexRef.current;

      if (
        isShiftPressed &&
        lastIndex !== null &&
        lastIndex !== undefined &&
        lastIndex >= 0 &&
        lastIndex < data.length
      ) {
        const startIndex = Math.min(lastIndex, currIndex);
        const endIndex = Math.max(lastIndex, currIndex);

        let rangeItems = data.slice(startIndex, endIndex + 1);
        if (isSelectable) {
          rangeItems = rangeItems.filter(isSelectable);
        }

        const rangeIds = rangeItems
          .map(getItemId)
          .filter((x) => x !== undefined && x !== null && x !== '');

        const isCurrentlySelected = selectedRowsId.includes(id);

        if (isCurrentlySelected) {
          const rangeSet = new Set(rangeIds);
          setSelectedRowsId((prev) => prev.filter((prevId) => !rangeSet.has(prevId)));
        } else {
          setSelectedRowsId((prev) => Array.from(new Set([...prev, ...rangeIds])));
        }
      } else {
        const isSelected = selectedRowsId.includes(id);
        if (isSelected) {
          setSelectedRowsId((prev) => prev.filter((prevId) => prevId !== id));
        } else {
          setSelectedRowsId((prev) => [...prev, id]);
        }
      }

      lastSelectedIndexRef.current = currIndex;
    },
    [data, selectedRowsId, setSelectedRowsId, getItemId, isSelectable]
  );

  return { handleSelectRow, lastSelectedIndexRef };
};

/**
 * Utility to generate dynamic columns from arbitrary row data array.
 */
export const generateDynamicColumns = (data = [], options = {}) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  const {
    ignoreKeys = [
      '_id',
      '__v',
      'client',
      'tenant',
      'user',
      'createdAt',
      'updatedAt',
      'is_draft',
      'additional_images',
    ],
    customWidths = {},
    customRenderers = {},
  } = options;

  const keyList = [];
  const keySet = new Set();

  data.forEach((row) => {
    if (row && typeof row === 'object') {
      Object.keys(row).forEach((k) => {
        if (ignoreKeys.includes(k)) return;
        if (!keySet.has(k)) {
          keySet.add(k);
          keyList.push({ key: k });
        }
      });
    }
  });

  return keyList.map(({ key }) => {
    const title = key
      .replace(/[-_]/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (c) => c.toUpperCase());

    const sampleRow =
      data.find((r) => r && r[key] !== undefined && r[key] !== null) || {};
    const sampleVal = sampleRow[key];
    const isNumber =
      typeof sampleVal === 'number' ||
      (!isNaN(sampleVal) && sampleVal !== '' && typeof sampleVal !== 'boolean');
    const isBoolean = typeof sampleVal === 'boolean';

    let align = isNumber ? 'text-right' : isBoolean ? 'text-center' : 'text-left';
    let headerClassName = 'min-w-[150px] max-w-[250px] px-3';
    let cellClassName = 'min-w-[150px] max-w-[250px] px-3 truncate';
    let width = 160;

    if (customWidths[key]) {
      const cw = customWidths[key];
      if (cw.headerClassName) headerClassName = cw.headerClassName;
      if (cw.cellClassName) cellClassName = cw.cellClassName;
      if (cw.align) align = cw.align;
      if (cw.width) width = typeof cw.width === 'number' ? cw.width : parseFloat(cw.width) || width;
    }

    return {
      key,
      id: key,
      title,
      align,
      width,
      headerClassName: `${headerClassName} whitespace-nowrap truncate`,
      cellClassName: `${cellClassName} whitespace-nowrap truncate`,
      render: (row) => {
        if (customRenderers[key]) {
          return customRenderers[key](row[key], row);
        }
        const val = row[key];
        if (val === null || val === undefined) return '-';
        if (typeof val === 'boolean') return val ? 'Yes' : 'No';
        if (typeof val === 'object') return JSON.stringify(val);
        return String(val);
      },
    };
  });
};

/**
 * CommonTable — Production TanStack Table with Dynamic Width Management (ResizeObserver)
 * and Hide/Show Management matching reference implementation.
 */
const CommonTable = ({
  tableId,
  enableManageFields = Boolean(tableId),
  isDynamic = true,
  columns = [],
  columnWidthByCount = DEFAULT_COLUMN_WIDTH_BY_COUNT,
  columnMaxWidthByCount = DEFAULT_COLUMN_MAX_WIDTH_BY_COUNT,
  data,
  loading = false,
  skeletonRows = 10,
  emptyMessage = 'No Records Found',
  rowClick,
  rowClassName,
  rowStyle,
  tableClassName = 'w-full',
  tableHeader,
  tableFooter,
  onSort,
  sortBy,
  orderBy,
  sortOptions,
  containerClassName = 'overflow-x-auto border border-[var(--border-color)] rounded-xl shadow-[var(--shadow-card)]',
  trClassName = 'cursor-pointer transition-colors hover:bg-[#F4F4F5] dark:hover:bg-zinc-800/50 group',
  theadTrClassName = 'bg-[#F4F4F5] dark:bg-zinc-900 border-b border-[var(--border-color)]',
  thClassName = 'px-3 text-left whitespace-nowrap font-semibold text-gray-700 dark:text-zinc-200 text-xs py-3',
  tdClassName = 'px-3 py-3 whitespace-nowrap text-xs',
}) => {
  const outerClassName = containerClassName.includes('overflow-x-auto')
    ? containerClassName.replace('overflow-x-auto', 'overflow-hidden')
    : containerClassName;

  const appliedTableClassName = tableClassName.includes('border-separate')
    ? tableClassName
    : `${tableClassName} border-separate border-spacing-0`;

  const isLoading = loading || data === undefined || data === null;
  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // Container width detection and horizontal scroll tracking
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth - 300 : 1200));
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);

  const checkScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const hasScroll = el.scrollWidth > el.clientWidth + 1;
    setCanScrollLeft(hasScroll && el.scrollLeft > 1);
    setCanScrollRight(hasScroll && el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const clientW = containerRef.current.clientWidth;
      if (clientW > 0) {
        setContainerWidth(clientW);
      }
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleUpdate = () => {
      if (containerRef.current) {
        const clientW = containerRef.current.clientWidth;
        if (clientW > 0) {
          setContainerWidth(clientW);
        }
      }
      checkScroll();
    };

    handleUpdate();
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', handleUpdate);

    const ro = new ResizeObserver(() => {
      handleUpdate();
    });

    ro.observe(el);
    const tableEl = el.querySelector('table');
    if (tableEl) {
      ro.observe(tableEl);
    }

    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', handleUpdate);
      ro.disconnect();
    };
  }, [checkScroll]);

  const userContext = useUser();
  const serverTables = userContext?.tableData || null;
  const serverSortData = userContext?.sortData || null;
  const updateTableSort = userContext?.updateTableSort;
  const updateTableColumns = userContext?.updateTableColumns;

  const activeDefaultFields = useMemo(() => {
    if (tableId && TABLE_DEFAULTS_MAP[tableId]) return TABLE_DEFAULTS_MAP[tableId];
    return null;
  }, [tableId]);

  // Compute customizable columns for ManageFields
  const defaultColumnDefs = useMemo(() => {
    return columns
      .filter((col) => {
        const id = (col.key || col.id || col.accessorKey || '').toString().toLowerCase();
        return (
          id !== 'checkbox' &&
          id !== 'selection' &&
          id !== 'select' &&
          id !== 'action' &&
          id !== 'actions' &&
          id !== 'drag_handle' &&
          id !== 'draghandle' &&
          id !== 'drag'
        );
      })
      .map((col, idx) => {
        const id = (col.key || col.id || col.accessorKey || `col_${idx}`).toString();
        const headerTitle = col.title !== undefined ? col.title : col.name !== undefined ? col.name : col.header || id;
        return {
          ...col,
          id,
          title: typeof headerTitle === 'string' ? headerTitle : col.name || id,
        };
      });
  }, [columns]);

  // Default Order
  const defaultOrder = useMemo(() => {
    const allIds = columns.map((col, idx) => (col.key || col.id || col.accessorKey || `col_${idx}`).toString());

    const dragHandleCols = [];
    const checkboxCols = [];
    const actionCols = [];
    const customizableIds = [];

    allIds.forEach((id) => {
      const lower = id.toLowerCase();
      if (lower === 'drag_handle' || lower === 'draghandle' || lower === 'drag') {
        dragHandleCols.push(id);
      } else if (lower === 'checkbox' || lower === 'selection' || lower === 'select') {
        checkboxCols.push(id);
      } else if (lower === 'action' || lower === 'actions') {
        actionCols.push(id);
      } else {
        customizableIds.push(id);
      }
    });

    let orderedCustom = customizableIds;
    if (activeDefaultFields && Array.isArray(activeDefaultFields)) {
      const ordered = [];
      const customSet = new Set(customizableIds);
      activeDefaultFields.forEach((id) => {
        if (customSet.has(id)) {
          ordered.push(id);
        }
      });
      customizableIds.forEach((id) => {
        if (!ordered.includes(id)) {
          ordered.push(id);
        }
      });
      orderedCustom = ordered;
    }

    return [...dragHandleCols, ...checkboxCols, ...orderedCustom, ...actionCols];
  }, [columns, activeDefaultFields]);

  // Default Visibility
  const defaultVisibility = useMemo(() => {
    const v = {};
    columns.forEach((col, idx) => {
      const id = (col.key || col.id || col.accessorKey || `col_${idx}`).toString();
      const lower = id.toLowerCase();
      const isFixed =
        lower === 'checkbox' ||
        lower === 'selection' ||
        lower === 'select' ||
        lower === 'drag_handle' ||
        lower === 'draghandle' ||
        lower === 'drag' ||
        lower === 'action' ||
        lower === 'actions';

      if (isFixed) {
        v[id] = true;
      } else if (activeDefaultFields && Array.isArray(activeDefaultFields)) {
        v[id] = activeDefaultFields.includes(id);
      } else {
        v[id] = col.visible !== false;
      }
    });
    return v;
  }, [columns, activeDefaultFields]);

  // Load Saved Config (Server Table Data + localStorage fallback)
  const loadSavedConfig = useCallback(() => {
    const validIds = new Set(defaultOrder);

    // 1. Try server tables if available
    if (tableId && serverTables && Array.isArray(serverTables[tableId]) && serverTables[tableId].length > 0) {
      try {
        const saved = serverTables[tableId];
        const savedVisibility = {};
        columns.forEach((col, idx) => {
          const id = (col.key || col.id || col.accessorKey || `col_${idx}`).toString();
          const lower = id.toLowerCase();
          const isFixed =
            lower === 'checkbox' ||
            lower === 'selection' ||
            lower === 'select' ||
            lower === 'drag_handle' ||
            lower === 'draghandle' ||
            lower === 'drag' ||
            lower === 'action' ||
            lower === 'actions';
          savedVisibility[id] = isFixed || saved.includes(id);
        });

        const savedOrder = [];
        const leftFixed = defaultOrder.filter((id) => {
          const lower = id.toLowerCase();
          return lower === 'drag_handle' || lower === 'draghandle' || lower === 'drag' || lower === 'checkbox' || lower === 'selection' || lower === 'select';
        });
        const rightFixed = defaultOrder.filter((id) => {
          const lower = id.toLowerCase();
          return lower === 'action' || lower === 'actions';
        });

        saved.forEach((id) => {
          if (validIds.has(id) && !leftFixed.includes(id) && !rightFixed.includes(id)) {
            savedOrder.push(id);
          }
        });
        defaultOrder.forEach((id) => {
          if (!savedOrder.includes(id) && !leftFixed.includes(id) && !rightFixed.includes(id)) {
            savedOrder.push(id);
          }
        });

        return {
          columnOrder: [...leftFixed, ...savedOrder, ...rightFixed],
          columnVisibility: savedVisibility,
        };
      } catch (e) { }
    }

    // 2. Fallback to localStorage
    if (tableId) {
      try {
        const storageKey = `table_config_${tableId}`;
        const savedStr = localStorage.getItem(storageKey);
        if (savedStr) {
          const savedObj = JSON.parse(savedStr);
          if (savedObj && typeof savedObj === 'object') {
            let savedOrder = Array.isArray(savedObj.columnOrder)
              ? savedObj.columnOrder.filter((id) => validIds.has(id))
              : [];

            defaultOrder.forEach((id) => {
              if (!savedOrder.includes(id)) {
                savedOrder.push(id);
              }
            });

            const savedVisibility = { ...defaultVisibility };
            if (savedObj.columnVisibility && typeof savedObj.columnVisibility === 'object') {
              Object.keys(savedObj.columnVisibility).forEach((id) => {
                if (validIds.has(id)) {
                  savedVisibility[id] = savedObj.columnVisibility[id];
                }
              });
            }

            return { columnOrder: savedOrder, columnVisibility: savedVisibility };
          }
        }
      } catch (e) { }
    }

    return { columnOrder: defaultOrder, columnVisibility: defaultVisibility };
  }, [tableId, serverTables, defaultOrder, defaultVisibility, columns]);

  const [columnOrder, setColumnOrder] = useState(() => loadSavedConfig().columnOrder);
  const [columnVisibility, setColumnVisibility] = useState(() => loadSavedConfig().columnVisibility);

  useEffect(() => {
    const { columnOrder: o, columnVisibility: v } = loadSavedConfig();
    setColumnOrder(o);
    setColumnVisibility(v);
  }, [tableId, loadSavedConfig, serverTables]);

  // Save Column Configuration
  const handleSaveConfig = useCallback(
    async (newConfig = {}) => {
      let updatedOrder = columnOrder;
      let updatedVisibility = columnVisibility;

      if (newConfig.columnOrder) {
        const leftFixed = defaultOrder.filter((id) => {
          const lower = id.toLowerCase();
          return lower === 'drag_handle' || lower === 'draghandle' || lower === 'drag' || lower === 'checkbox' || lower === 'selection' || lower === 'select';
        });
        const rightFixed = defaultOrder.filter((id) => {
          const lower = id.toLowerCase();
          return lower === 'action' || lower === 'actions';
        });
        const midCols = newConfig.columnOrder.filter((id) => !leftFixed.includes(id) && !rightFixed.includes(id));
        updatedOrder = [...leftFixed, ...midCols, ...rightFixed];
      }
      if (newConfig.columnVisibility) updatedVisibility = newConfig.columnVisibility;

      setColumnOrder(updatedOrder);
      setColumnVisibility(updatedVisibility);

      let visibleColumnsArray = updatedOrder.filter((key) => {
        const lower = key.toLowerCase();
        const isFixed = lower === 'checkbox' || lower === 'selection' || lower === 'select' || lower === 'drag_handle' || lower === 'draghandle' || lower === 'drag' || lower === 'action' || lower === 'actions';
        return !isFixed && updatedVisibility[key] !== false;
      });

      if (tableId && updateTableColumns) {
        updateTableColumns(tableId, visibleColumnsArray);
      }
    },
    [columnOrder, columnVisibility, tableId, defaultOrder, updateTableColumns]
  );

  // Reset Column Configuration
  const handleResetConfig = useCallback(async () => {
    setColumnOrder(defaultOrder);
    setColumnVisibility(defaultVisibility);

    if (tableId && updateTableColumns) {
      updateTableColumns(tableId, []);
    }
  }, [defaultOrder, defaultVisibility, tableId, updateTableColumns]);

  const savedSort = useMemo(() => {
    if (!tableId) return null;
    if (serverSortData && (serverSortData[tableId] || serverSortData[tableId?.toLowerCase()])) {
      return serverSortData[tableId] || serverSortData[tableId?.toLowerCase()];
    }
    return null;
  }, [tableId, serverSortData]);

  const effectiveSortBy = sortBy !== undefined && sortBy !== '' ? sortBy : savedSort?.sort_by || '';
  const effectiveOrderBy = orderBy !== undefined ? orderBy : savedSort?.order_by || 'DESC';

  // Handle Sort and persist sort_data directly to /admin/user/UpdateTablesData using tableId
  const handleTableSort = useCallback(
    async (field, direction, isFromPanel = false) => {
      let nextSortBy = effectiveSortBy;
      if (field) {
        nextSortBy = field;
      }

      let nextOrderByValue = 'DESC';
      if (direction) {
        nextOrderByValue = isAscendingOrder(direction) ? 'ASC' : 'DESC';
      } else if (effectiveSortBy === nextSortBy) {
        const isCurrentlyAsc = isAscendingOrder(effectiveOrderBy);
        nextOrderByValue = isCurrentlyAsc ? 'DESC' : 'ASC';
      } else {
        nextOrderByValue = 'DESC';
      }

      // Immediately trigger parent onSort callback
      if (onSort) {
        onSort(nextSortBy, nextOrderByValue);
      }

      // Save sort_data to UserContext & backend ONLY if triggered from the Sort by panel
      if (isFromPanel && tableId && updateTableSort) {
        updateTableSort(tableId, {
          sort_by: nextSortBy,
          order_by: nextOrderByValue,
        });
      }
    },
    [effectiveSortBy, effectiveOrderBy, onSort, tableId, updateTableSort]
  );

  const handleTableSortRef = useRef(handleTableSort);
  useEffect(() => {
    handleTableSortRef.current = handleTableSort;
  }, [handleTableSort]);

  // Calculate visible count
  const visibleColumnCount = useMemo(() => {
    return columns.filter((col, idx) => {
      const id = (col.key || col.id || col.accessorKey || `col_${idx}`).toString();
      return columnVisibility[id] !== false;
    }).length || columns.length || 5;
  }, [columns, columnVisibility]);

  // Convert incoming columns into TanStack Table column definitions
  const tanstackColumns = useMemo(() => {
    const dynamicColumnWidth = isDynamic
      ? getWidthByColumnCount(visibleColumnCount, columnWidthByCount, 220)
      : undefined;
    const dynamicColumnMaxWidth = isDynamic
      ? getWidthByColumnCount(visibleColumnCount, columnMaxWidthByCount, 550)
      : undefined;

    return columns.map((col, colIndex) => {
      let rawId = (col.key || col.id || col.accessorKey || '').toString();
      if (!rawId) {
        if (colIndex === 0) rawId = 'checkbox';
        else rawId = `col_${colIndex}`;
      }
      const colId = rawId;
      const keyLower = colId.toLowerCase();

      const isCheckbox = keyLower === 'checkbox' || keyLower === 'selection' || keyLower === 'select';
      const isDrag = keyLower === 'drag_handle' || keyLower === 'draghandle' || keyLower === 'drag';
      const isImage = keyLower === 'image' || keyLower === 'product_image';
      const isAction = keyLower === 'action' || keyLower === 'actions';
      const isFixedSystemCol = isCheckbox || isDrag || isAction || isImage;

      const hasExplicitWidth = col.width !== undefined || col.size !== undefined || col?.style?.width !== undefined;
      const hasExplicitMaxWidth = col.maxWidth !== undefined || col.maxSize !== undefined || col?.style?.maxWidth !== undefined;
      const hasExplicitMinWidth = col.minWidth !== undefined || col.minSize !== undefined || col?.style?.minWidth !== undefined;

      const isFixed = isCheckbox || isDrag || isImage || isAction;

      let fixedWidth = undefined;
      if (isCheckbox) {
        fixedWidth = hasExplicitWidth ? parseSize(col.width || col.size) : 50;
      } else if (isDrag) {
        fixedWidth = hasExplicitWidth ? parseSize(col.width || col.size) : 40;
      } else if (isImage) {
        fixedWidth = hasExplicitWidth ? parseSize(col.width || col.size) : 75;
      } else if (isAction) {
        fixedWidth = hasExplicitWidth ? parseSize(col.width || col.size) : 150;
      }

      let defaultSize = isFixed ? fixedWidth : (isDynamic ? dynamicColumnWidth : undefined);
      let defaultMaxSize = isFixed ? fixedWidth : (isDynamic ? dynamicColumnMaxWidth : undefined);
      let defaultMinSize = isFixed ? fixedWidth : undefined;
      let defaultFlexWeight = isFixed ? 0 : 1;

      const rawWidth = col.width ?? col.size ?? col?.style?.width;
      const rawMaxWidth = col.maxWidth ?? col.maxSize ?? col?.style?.maxWidth;
      const rawMinWidth = col.minWidth ?? col.minSize ?? col?.style?.minWidth;

      const size = isFixed ? fixedWidth : (hasExplicitWidth ? parseSize(rawWidth) : defaultSize);
      const maxSize = isFixed ? (hasExplicitMaxWidth ? parseSize(rawMaxWidth) : fixedWidth) : (hasExplicitMaxWidth ? parseSize(rawMaxWidth) : defaultMaxSize);
      const minSize = isFixed ? (hasExplicitMinWidth ? parseSize(rawMinWidth) : fixedWidth) : (hasExplicitMinWidth ? parseSize(rawMinWidth) : defaultMinSize);
      const flexWeight = isFixed ? 0 : (col.flexWeight !== undefined ? col.flexWeight : defaultFlexWeight);

      const headerTitle = col.title !== undefined ? col.title : col.name !== undefined ? col.name : col.header || colId;

      return {
        id: colId,
        accessorKey: col.accessorKey || col.key || colId,
        header: headerTitle,
        title: typeof headerTitle === 'string' ? headerTitle : col.name || colId,
        size,
        maxSize,
        minSize,
        flexWeight,
        isFixed,
        hasExplicitWidth,
        enableHiding: col.hideable !== false && !isFixedSystemCol,
        align: col.align || (isCheckbox || isDrag || isImage ? 'text-center' : isAction ? 'text-right' : 'text-left'),
        headerClassName: col.headerClassName || '',
        cellClassName: col.cellClassName || '',
        className: col.className || '',
        ignoreBaseClassName: col.ignoreBaseClassName,
        ignoreRowClick: col.ignoreRowClick,
        sortable: col.sortable !== false,
        rawCol: col,
        cell: ({ row }) => {
          if (col.render) {
            return col.render(row.original, row.index);
          }
          if (col.cell && typeof col.cell === 'function') {
            return col.cell(row.original, row.index);
          }
          const val = row.original?.[colId];
          if (val === null || val === undefined) return '-';
          if (typeof val === 'boolean') return val ? 'Yes' : 'No';
          if (typeof val === 'object') return JSON.stringify(val);
          return String(val);
        },
      };
    });
  }, [columns, visibleColumnCount, columnWidthByCount, columnMaxWidthByCount, isDynamic]);

  // Dynamic Column Width Calculation based on currently visible columns
  const computedColumnSizing = useMemo(() => {
    if (!isDynamic) return {};

    const activeOrder = columnOrder.length > 0 ? columnOrder : tanstackColumns.map((c) => c.id);
    const colDefMap = new Map(tanstackColumns.map((c) => [c.id, c]));

    const visibleCols = activeOrder
      .filter((id) => columnVisibility[id] !== false && colDefMap.has(id))
      .map((id) => colDefMap.get(id));

    if (visibleCols.length === 0) return {};

    let fixedSum = 0;
    let flexSum = 0;
    let totalWeight = 0;

    visibleCols.forEach((col) => {
      if (col.isFixed) {
        fixedSum += (col.size || 0);
      } else {
        flexSum += (col.size || 0);
        totalWeight += (col.flexWeight || 1);
      }
    });

    const totalBaseSum = fixedSum + flexSum;
    const isOverflowing = totalBaseSum > containerWidth;
    const availableExtra = (!isOverflowing && containerWidth > totalBaseSum) ? containerWidth - totalBaseSum : 0;

    const sizingMap = {};
    visibleCols.forEach((col) => {
      if (col.isFixed) {
        sizingMap[col.id] = col.size;
      } else {
        if (!isOverflowing && availableExtra > 0 && totalWeight > 0) {
          const extra = Math.floor(availableExtra * ((col.flexWeight || 1) / totalWeight));
          sizingMap[col.id] = (col.size || 0) + extra;
        } else {
          sizingMap[col.id] = col.size;
        }
      }
    });

    return sizingMap;
  }, [isDynamic, tanstackColumns, columnOrder, columnVisibility, containerWidth]);

  // TanStack Table Instance
  const table = useReactTable({
    data: safeData,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility,
      columnOrder,
      columnSizing: computedColumnSizing,
    },
    onColumnVisibilityChange: (updaterOrValue) => {
      const nextVis = typeof updaterOrValue === 'function' ? updaterOrValue(columnVisibility) : updaterOrValue;
      handleSaveConfig({ columnVisibility: nextVis });
    },
    onColumnOrderChange: (updaterOrValue) => {
      const nextOrder = typeof updaterOrValue === 'function' ? updaterOrValue(columnOrder) : updaterOrValue;
      handleSaveConfig({ columnOrder: nextOrder });
    },
  });

  // Auto-sync scroll states
  useEffect(() => {
    const timer = setTimeout(() => {
      checkScroll();
    }, 60);
    checkScroll();
    return () => clearTimeout(timer);
  }, [checkScroll, safeData, columnVisibility, computedColumnSizing, containerWidth]);

  // Calculate sticky offset positions
  const stickyColumnStyles = useMemo(() => {
    const visibleCols = table.getVisibleLeafColumns();
    const map = {};

    const getColWidth = (col) => {
      if (computedColumnSizing[col.id]) return computedColumnSizing[col.id];
      const rawCol = col.columnDef?.rawCol || {};
      const rawWidth = rawCol.width ?? rawCol.size ?? rawCol?.style?.width;
      if (rawWidth !== undefined) return parseSize(rawWidth);
      if (col.columnDef?.size !== undefined) return col.columnDef.size;
      const lower = col.id.toLowerCase();
      if (lower === 'drag_handle' || lower === 'draghandle' || lower === 'drag') return 40;
      if (lower === 'checkbox' || lower === 'selection' || lower === 'select') return 50;
      if (lower === 'action' || lower === 'actions') return 150;
      return col.getSize() || 150;
    };

    const leftCols = [];
    for (let i = 0; i < visibleCols.length; i++) {
      const col = visibleCols[i];
      const lower = col.id.toLowerCase();
      const rawCol = col.columnDef?.rawCol || {};
      const isLeftFixed =
        rawCol.fixed === 'left' ||
        rawCol.sticky === 'left' ||
        lower === 'drag_handle' ||
        lower === 'draghandle' ||
        lower === 'drag' ||
        lower === 'checkbox' ||
        lower === 'selection' ||
        lower === 'select';

      if (isLeftFixed) {
        leftCols.push(col);
      } else {
        break;
      }
    }

    const rightCols = [];
    for (let i = visibleCols.length - 1; i >= 0; i--) {
      const col = visibleCols[i];
      const lower = col.id.toLowerCase();
      const rawCol = col.columnDef?.rawCol || {};
      const isRightFixed =
        rawCol.fixed === 'right' ||
        rawCol.sticky === 'right' ||
        lower === 'action' ||
        lower === 'actions';

      if (isRightFixed) {
        rightCols.unshift(col);
      } else {
        break;
      }
    }

    let leftOffset = 0;
    leftCols.forEach((col, idx) => {
      const colWidth = getColWidth(col);
      map[col.id] = {
        isSticky: true,
        position: 'left',
        offset: leftOffset,
        isEdge: idx === leftCols.length - 1,
      };
      leftOffset += colWidth;
    });

    let rightOffset = 0;
    for (let i = rightCols.length - 1; i >= 0; i--) {
      const col = rightCols[i];
      const colWidth = getColWidth(col);
      map[col.id] = {
        isSticky: true,
        position: 'right',
        offset: rightOffset,
        isEdge: i === 0,
      };
      rightOffset += colWidth;
    }

    return map;
  }, [table, computedColumnSizing]);

  const getColumnWidthStyle = useCallback(
    (colDef, colId, stickyStyle = {}) => {
      if (isDynamic) {
        const colWidth = computedColumnSizing[colId] || colDef?.size;
        const keyLower = (colId || '').toLowerCase();
        const isAction = keyLower === 'action' || keyLower === 'actions';

        if (isAction) {
          const isAuto = colDef?.rawCol?.width === 'auto' || colDef?.size === 'auto' || colWidth === 'auto';
          const explicitWidth = !isAuto && colWidth !== undefined ? (typeof colWidth === 'number' ? `${colWidth}px` : colWidth) : undefined;
          return {
            ...(explicitWidth ? { width: explicitWidth } : isAuto ? { width: 'auto' } : {}),
            ...(colDef?.minSize ? { minWidth: `${colDef.minSize}px` } : {}),
            ...(colDef?.maxSize ? { maxWidth: `${colDef.maxSize}px` } : {}),
            ...stickyStyle,
          };
        }

        if (colDef?.isFixed) {
          return {
            width: `${colWidth}px`,
            minWidth: `${colWidth}px`,
            maxWidth: `${colWidth}px`,
            ...stickyStyle,
          };
        }

        return {
          ...(colWidth ? { width: `${colWidth}px` } : {}),
          ...(colDef?.minSize ? { minWidth: `${colDef.minSize}px` } : {}),
          ...(colDef?.maxSize ? { maxWidth: `${colDef.maxSize}px` } : {}),
          ...stickyStyle,
        };
      }

      const rawCol = colDef?.rawCol || {};
      const formatVal = (v) => (typeof v === 'number' ? `${v}px` : v);
      const width = rawCol.width ?? rawCol.size ?? rawCol?.style?.width;
      const minWidth = rawCol.minWidth ?? rawCol.minSize ?? rawCol?.style?.minWidth;
      const maxWidth = rawCol.maxWidth ?? rawCol.maxSize ?? rawCol?.style?.maxWidth;

      return {
        ...(rawCol.style || {}),
        ...(width ? { width: formatVal(width) } : {}),
        ...(minWidth ? { minWidth: formatVal(minWidth) } : {}),
        ...(maxWidth ? { maxWidth: formatVal(maxWidth) } : {}),
        ...stickyStyle,
      };
    },
    [isDynamic, computedColumnSizing]
  );

  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);

  const renderManageFieldsButton = useCallback(() => {
    if (!enableManageFields || !tableId) return null;
    return (
      <ManageFieldsButton
        tableId={tableId}
        allowedSortKeys={TABLE_WISE_ALLOWED_SORT_KEYS[tableId]}
        anchorEl={popoverAnchorEl}
        onOpen={(e) => setPopoverAnchorEl(e.currentTarget)}
        onClose={() => setPopoverAnchorEl(null)}
        defaultColumnDefs={defaultColumnDefs}
        columnOrder={columnOrder.filter((id) => {
          const lower = id.toLowerCase();
          return (
            lower !== 'checkbox' &&
            lower !== 'selection' &&
            lower !== 'select' &&
            lower !== 'drag_handle' &&
            lower !== 'draghandle' &&
            lower !== 'drag' &&
            lower !== 'action' &&
            lower !== 'actions'
          );
        })}
        columnVisibility={columnVisibility}
        onSaveConfig={handleSaveConfig}
        onResetConfig={handleResetConfig}
        sortBy={effectiveSortBy}
        orderBy={effectiveOrderBy}
        onSort={handleTableSort}
        sortOptions={sortOptions}
      />
    );
  }, [
    enableManageFields,
    tableId,
    popoverAnchorEl,
    defaultColumnDefs,
    columnOrder,
    columnVisibility,
    handleSaveConfig,
    handleResetConfig,
    effectiveSortBy,
    effectiveOrderBy,
    handleTableSort,
    sortOptions,
  ]);

  const renderedHeader =
    typeof tableHeader === 'function'
      ? tableHeader({ manageFieldsBtn: renderManageFieldsButton() })
      : tableHeader;

  return (
    <div className={outerClassName}>
      {renderedHeader}
      <div
        ref={containerRef}
        className="overflow-x-auto w-full overflow-y-hidden custom-scrollbar relative"
      >
        <table className={appliedTableClassName}>
          {isDynamic && (
            <colgroup>
              {table.getVisibleLeafColumns().map((column) => {
                const colDef = column.columnDef;
                const colId = column.id;
                const keyLower = colId.toLowerCase();
                const isAction = keyLower === 'action' || keyLower === 'actions';
                const isFixed = colDef.isFixed;
                const colWidth = computedColumnSizing[colId] || colDef.size;

                if (isAction) {
                  const isAuto = colDef?.rawCol?.width === 'auto' || colDef?.size === 'auto' || colWidth === 'auto';
                  const explicitWidth = !isAuto && colWidth !== undefined ? `${colWidth}px` : isAuto ? 'auto' : undefined;
                  return (
                    <col
                      key={colId}
                      style={{
                        ...(explicitWidth ? { width: explicitWidth } : {}),
                        minWidth: colDef.minSize ? `${colDef.minSize}px` : '150px',
                      }}
                    />
                  );
                }

                if (isFixed) {
                  return (
                    <col
                      key={colId}
                      style={{
                        width: colWidth ? `${colWidth}px` : undefined,
                        minWidth: colDef.minSize ? `${colDef.minSize}px` : undefined,
                        maxWidth: colDef.maxSize ? `${colDef.maxSize}px` : undefined,
                      }}
                    />
                  );
                }

                return (
                  <col
                    key={colId}
                    style={{
                      width: colWidth ? `${colWidth}px` : undefined,
                      minWidth: colDef.minSize ? `${colDef.minSize}px` : undefined,
                      maxWidth: colDef.maxSize ? `${colDef.maxSize}px` : undefined,
                    }}
                  />
                );
              })}
            </colgroup>
          )}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className={theadTrClassName}>
                {headerGroup.headers.map((header) => {
                  const colDef = header.column.columnDef;
                  const colKey = header.id;
                  const keyLower = colKey.toLowerCase();

                  const isIgnored =
                    keyLower === 'checkbox' ||
                    keyLower === 'image' ||
                    keyLower === 'action' ||
                    keyLower === 'actions' ||
                    keyLower === 'drag_handle' ||
                    keyLower === 'draghandle' ||
                    keyLower === 'drag' ||
                    colDef.sortable === false;

                  const allowedTableSortKeys = tableId && TABLE_WISE_ALLOWED_SORT_KEYS[tableId]
                    ? TABLE_WISE_ALLOWED_SORT_KEYS[tableId]
                    : null;

                  const isKeyAllowedToSort = allowedTableSortKeys
                    ? allowedTableSortKeys.some((k) => k.toLowerCase() === colKey.toLowerCase() || k.toLowerCase() === keyLower)
                    : Boolean(onSort);

                  const isSortable = isKeyAllowedToSort && !isIgnored;

                  let renderedHeader = flexRender(colDef.header, header.getContext());

                  if (isSortable && (typeof renderedHeader === 'string' || typeof renderedHeader === 'number' || typeof colDef.title === 'string')) {
                    const isSorted = (sortBy || effectiveSortBy) === colKey;
                    const isAsc = isAscendingOrder(effectiveOrderBy);

                    const alignClass =
                      colDef.align === 'text-right' || (colDef.headerClassName && colDef.headerClassName.includes('text-right'))
                        ? 'justify-end'
                        : colDef.align === 'text-center' || (colDef.headerClassName && colDef.headerClassName.includes('text-center'))
                          ? 'justify-center'
                          : 'justify-start';

                    renderedHeader = (
                      <div className={`flex items-center w-full ${alignClass}`}>
                        <button
                          type="button"
                          className={`inline-flex items-center gap-1 transition-colors select-none group cursor-pointer whitespace-nowrap ${isSorted
                            ? 'font-bold text-[var(--primary-text-color)]'
                            : 'text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]'
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTableSort(colKey);
                          }}
                        >
                          <span className="whitespace-nowrap truncate">{renderedHeader}</span>
                          {isSorted ? (
                            isAsc ? (
                              <CaretUpFill className="w-3 h-3 shrink-0 text-[var(--primary-text-color)] ms-0.5" />
                            ) : (
                              <CaretDownFill className="w-3 h-3 shrink-0 text-[var(--primary-text-color)] ms-0.5" />
                            )
                          ) : (
                            <CaretUpFill className="w-3 h-3 shrink-0 text-[var(--table-text-color)] ms-0.5 opacity-0 group-hover:opacity-60 transition-opacity" />
                          )}
                        </button>
                      </div>
                    );
                  }

                  const isImageCol = keyLower === 'image' || keyLower === 'product_image';
                  const isCheckboxCol = keyLower === 'checkbox' || keyLower === 'selection' || keyLower === 'select';
                  const isDragCol = keyLower === 'drag_handle' || keyLower === 'draghandle' || keyLower === 'drag';

                  const cleanHeaderCls = sanitizeWidthClasses(colDef.headerClassName || colDef.className || '');
                  const baseThCls = (isCheckboxCol || isDragCol)
                    ? `${thClassName} !px-0 text-center`
                    : isImageCol
                      ? `${thClassName} !px-2 text-center`
                      : thClassName;
                  const stickyInfo = stickyColumnStyles[header.id];
                  const isSticky = Boolean(stickyInfo?.isSticky);
                  const isRightSticky = stickyInfo?.position === 'right';
                  const isLeftSticky = stickyInfo?.position === 'left';
                  const isEdge = Boolean(stickyInfo?.isEdge);

                  const shadowClass = (isRightSticky && isEdge && canScrollRight)
                    ? 'border-l border-[var(--border-color)] shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]'
                    : (isLeftSticky && isEdge && canScrollLeft)
                      ? 'border-r border-[var(--border-color)] shadow-[4px_0_6px_-2px_rgba(0,0,0,0.06)]'
                      : '';

                  const stickyStyle = isSticky
                    ? {
                      position: 'sticky',
                      [stickyInfo.position]: `${stickyInfo.offset}px`,
                      zIndex: 25,
                    }
                    : {};
                  const stickyClass = isSticky
                    ? `sticky-table-header ${shadowClass}`.trim()
                    : '';

                  const appliedThClassName = colDef.ignoreBaseClassName
                    ? `${colDef.align || 'text-left'} ${cleanHeaderCls} ${stickyClass} whitespace-nowrap ${isSticky ? '' : 'overflow-hidden truncate text-ellipsis'} relative`
                    : `${baseThCls} ${colDef.align || 'text-left'} ${cleanHeaderCls} ${stickyClass} whitespace-nowrap ${isSticky ? '' : 'overflow-hidden truncate text-ellipsis'} relative`;

                  const widthStyle = getColumnWidthStyle(colDef, header.id, stickyStyle);

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={appliedThClassName}
                      style={widthStyle}
                    >
                      {renderedHeader}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {isLoading ? (
              Array.from({ length: skeletonRows || 10 }).map((_, rowIndex) => (
                <tr
                  key={`skeleton-${rowIndex}`}
                  className="border-b border-[var(--border-color)] animate-pulse"
                >
                  {table.getVisibleLeafColumns().map((column, colIndex) => {
                    const colDef = column.columnDef;
                    const colKey = column.id.toLowerCase();
                    const isCheckbox = colKey === 'checkbox' || colKey === 'selection' || colKey === 'select';
                    const isDrag = colKey === 'drag_handle' || colKey === 'draghandle' || colKey === 'drag';
                    const isImage = colKey === 'image' || colKey === 'product_image';

                    const cleanCellCls = sanitizeWidthClasses(colDef.cellClassName || colDef.className || '');
                    const baseTdCls = (isCheckbox || isDrag) ? '!px-0 text-center' : isImage ? 'px-2 text-center' : tdClassName;

                    const stickyInfo = stickyColumnStyles[column.id];
                    const isSticky = Boolean(stickyInfo?.isSticky);
                    const isRightSticky = stickyInfo?.position === 'right';
                    const isLeftSticky = stickyInfo?.position === 'left';
                    const isEdge = Boolean(stickyInfo?.isEdge);

                    const shadowClass = (isRightSticky && isEdge && canScrollRight)
                      ? 'border-l border-[var(--border-color)] shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]'
                      : (isLeftSticky && isEdge && canScrollLeft)
                        ? 'border-r border-[var(--border-color)] shadow-[4px_0_6px_-2px_rgba(0,0,0,0.06)]'
                        : '';

                    const stickyStyle = isSticky
                      ? {
                        position: 'sticky',
                        [stickyInfo.position]: `${stickyInfo.offset}px`,
                        zIndex: 15,
                      }
                      : {};
                    const stickyClass = isSticky
                      ? `sticky-table-cell ${shadowClass}`.trim()
                      : '';

                    const appliedTdClassName = colDef.ignoreBaseClassName
                      ? `${colDef.align || 'text-left'} ${cleanCellCls} ${stickyClass} whitespace-nowrap ${isSticky ? '' : 'overflow-hidden truncate text-ellipsis'}`
                      : `${baseTdCls} ${colDef.align || 'text-left'} ${cleanCellCls} ${stickyClass} whitespace-nowrap ${isSticky ? '' : 'overflow-hidden truncate text-ellipsis'}`;

                    const widthStyle = getColumnWidthStyle(colDef, column.id, stickyStyle);

                    const alignClass =
                      colDef.align === 'text-right' || appliedTdClassName.includes('text-right')
                        ? 'justify-end'
                        : colDef.align === 'text-center' || appliedTdClassName.includes('text-center')
                          ? 'justify-center'
                          : 'justify-start';

                    return (
                      <td
                        key={`skeleton-td-${colIndex}`}
                        className={appliedTdClassName}
                        style={widthStyle}
                      >
                        <div
                          className={`flex items-center w-full h-[40px] ${isCheckbox || isDrag
                            ? 'justify-center'
                            : isImage
                              ? 'justify-center px-2'
                              : alignClass
                            }`}
                        >
                          {isCheckbox ? (
                            <div className="w-[18px] h-[18px] rounded bg-gray-200 dark:bg-zinc-800 shrink-0" />
                          ) : isDrag ? (
                            <div className="w-[18px] h-[18px] rounded bg-gray-200 dark:bg-zinc-800 shrink-0" />
                          ) : isImage ? (
                            <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-zinc-800 shrink-0" />
                          ) : (
                            <div
                              className={`h-4 rounded bg-gray-200 dark:bg-zinc-800 ${colIndex % 3 === 0
                                ? 'w-4/5'
                                : colIndex % 3 === 1
                                  ? 'w-3/5'
                                  : 'w-2/5'
                                }`}
                            />
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, index) => {
                const appliedRowClassName = rowClassName
                  ? typeof rowClassName === 'function'
                    ? rowClassName(row.original, index)
                    : rowClassName
                  : trClassName;

                const appliedRowStyle = rowStyle
                  ? typeof rowStyle === 'function'
                    ? rowStyle(row.original, index)
                    : rowStyle
                  : undefined;

                return (
                  <tr
                    key={row.id || index}
                    className={appliedRowClassName}
                    style={appliedRowStyle}
                    onClick={(e) => {
                      window.__currentClickEvent = e;
                      try {
                        rowClick?.(row.original, e);
                      } finally {
                        setTimeout(() => {
                          if (window.__currentClickEvent === e) {
                            window.__currentClickEvent = null;
                          }
                        }, 100);
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const colDef = cell.column.columnDef;
                      const colKey = cell.column.id.toLowerCase();
                      const isCheckbox = colKey === 'checkbox' || colKey === 'selection' || colKey === 'select';
                      const isDrag = colKey === 'drag_handle' || colKey === 'draghandle' || colKey === 'drag';
                      const isImage = colKey === 'image' || colKey === 'product_image';

                      const cleanCellCls = sanitizeWidthClasses(colDef.cellClassName || colDef.className || '');
                      const baseTdCls = (isCheckbox || isDrag) ? '!px-0 text-center' : isImage ? 'px-2 text-center' : tdClassName;

                      const stickyInfo = stickyColumnStyles[cell.column.id];
                      const isSticky = Boolean(stickyInfo?.isSticky);
                      const isRightSticky = stickyInfo?.position === 'right';
                      const isLeftSticky = stickyInfo?.position === 'left';
                      const isEdge = Boolean(stickyInfo?.isEdge);

                      const shadowClass = (isRightSticky && isEdge && canScrollRight)
                        ? 'border-l border-[var(--border-color)] shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]'
                        : (isLeftSticky && isEdge && canScrollLeft)
                          ? 'border-r border-[var(--border-color)] shadow-[4px_0_6px_-2px_rgba(0,0,0,0.06)]'
                          : '';

                      const stickyStyle = isSticky
                        ? {
                          position: 'sticky',
                          [stickyInfo.position]: `${stickyInfo.offset}px`,
                          zIndex: 15,
                        }
                        : {};
                      const stickyClass = isSticky
                        ? `sticky-table-cell ${shadowClass}`.trim()
                        : '';
                      const appliedTdClassName = colDef.ignoreBaseClassName
                        ? `${colDef.align || 'text-left'} ${cleanCellCls} ${stickyClass} whitespace-nowrap ${isSticky ? '' : 'overflow-hidden truncate text-ellipsis'}`
                        : `${baseTdCls} ${colDef.align || 'text-left'} ${cleanCellCls} ${stickyClass} whitespace-nowrap ${isSticky ? '' : 'overflow-hidden truncate text-ellipsis'}`;

                      const widthStyle = getColumnWidthStyle(colDef, cell.column.id, stickyStyle);

                      return (
                        <td
                          key={cell.id}
                          className={appliedTdClassName}
                          style={widthStyle}
                          onClick={colDef.ignoreRowClick ? (e) => e.stopPropagation() : undefined}
                        >
                          {flexRender(colDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr className="pointer-events-none">
                <td
                  colSpan={table.getVisibleLeafColumns().length || columns.length || 1}
                  style={{ width: '100%', minWidth: '100%' }}
                  className="text-center py-6 text-sm text-[var(--table-text-color)]"
                >
                  <div className="flex items-center justify-center w-full text-center">
                    {React.isValidElement(emptyMessage)
                      ? React.cloneElement(emptyMessage, { isTable: true })
                      : emptyMessage}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {tableFooter && tableFooter}
    </div>
  );
};

export default CommonTable;